/**
 * Asset Cache Service
 * CDN caching, model compression hints, and progressive loading for GLB/OBJ assets.
 * Optimized for mobile and variable Abidjan connectivity.
 */

// ─── Constants ─────────────────────────────────────────────────────────────────
const CACHE_VERSION = 'v2';
const CACHE_PREFIX = `kapital_3d_${CACHE_VERSION}_`;
const CACHE_META_KEY = `kapital_3d_meta_${CACHE_VERSION}`;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_CACHE_ENTRIES = 80;
const MAX_MEMORY_ENTRIES = 30;

// In-memory LRU for session-level instant access
const memoryCache = new Map();

// ─── Network Quality Detection ─────────────────────────────────────────────────

/**
 * Detects effective network type and returns quality tier.
 * Useful for Abidjan's variable connectivity (2G/3G/4G mix).
 * @returns {'slow'|'medium'|'fast'}
 */
export const getNetworkQuality = () => {
  try {
    const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    if (!conn) return 'medium';

    const { effectiveType, downlink, saveData } = conn;

    if (saveData) return 'slow';
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'slow';
    if (effectiveType === '3g' || (downlink && downlink < 2)) return 'medium';
    return 'fast';
  } catch {
    return 'medium';
  }
};

/**
 * Returns recommended GL settings based on network + device quality.
 * @returns {{ dpr: number, antialias: boolean, shadowMapSize: number, maxTextureSize: number }}
 */
export const getAdaptiveRenderSettings = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent || '');
  const quality = getNetworkQuality();
  const pixelRatio = window?.devicePixelRatio || 1;

  if (isMobile || quality === 'slow') {
    return {
      dpr: Math.min(pixelRatio, 1.0),
      antialias: false,
      shadowMapSize: 256,
      maxTextureSize: 512,
      quality: 'low',
    };
  }

  if (quality === 'medium') {
    return {
      dpr: Math.min(pixelRatio, 1.5),
      antialias: false,
      shadowMapSize: 512,
      maxTextureSize: 1024,
      quality: 'medium',
    };
  }

  return {
    dpr: Math.min(pixelRatio, 2.0),
    antialias: true,
    shadowMapSize: 1024,
    maxTextureSize: 2048,
    quality: 'high',
  };
};

// ─── Cache Helpers ─────────────────────────────────────────────────────────────

const getCacheMeta = () => {
  try {
    const raw = localStorage.getItem(CACHE_META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveCacheMeta = (meta) => {
  try {
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch {
    evictOldestEntries(10);
  }
};

const evictOldestEntries = (count = 5) => {
  try {
    const meta = getCacheMeta();
    const sorted = Object.entries(meta).sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    for (const [key] of sorted.slice(0, count)) {
      localStorage.removeItem(CACHE_PREFIX + key);
      delete meta[key];
    }
    localStorage.setItem(CACHE_META_KEY, JSON.stringify(meta));
  } catch {
    // Ignore
  }
};

/**
 * Generates a stable cache key from a URL or storage path.
 * @param {string} pathOrUrl
 * @returns {string}
 */
export const getCacheKey = (pathOrUrl) => {
  if (!pathOrUrl) return '';
  try {
    const url = new URL(pathOrUrl);
    return url.pathname.replace(/^\/+/, '').replace(/\//g, '_').slice(-80);
  } catch {
    return pathOrUrl.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
  }
};

/**
 * Stores a resolved asset URL in memory + localStorage with TTL.
 * @param {string} key
 * @param {string} url
 * @param {object} [meta]
 */
export const cacheAssetUrl = (key, url, meta = {}) => {
  if (!key || !url) return;
  const now = Date.now();

  // Memory LRU
  if (memoryCache.size >= MAX_MEMORY_ENTRIES) {
    memoryCache.delete(memoryCache.keys().next().value);
  }
  memoryCache.set(key, { url, cachedAt: now });

  // Persistent cache
  try {
    const cacheMeta = getCacheMeta();
    const keys = Object.keys(cacheMeta);
    if (keys.length >= MAX_CACHE_ENTRIES) evictOldestEntries(10);

    cacheMeta[key] = {
      cachedAt: now,
      lastAccessed: now,
      expiresAt: now + CACHE_TTL_MS,
      ...meta,
    };
    saveCacheMeta(cacheMeta);
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ url, cachedAt: now }));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Retrieves a cached asset URL. Returns null if missing or expired.
 * @param {string} key
 * @returns {string|null}
 */
export const getCachedAssetUrl = (key) => {
  if (!key) return null;

  // Memory first
  if (memoryCache.has(key)) {
    return memoryCache.get(key)?.url || null;
  }

  try {
    const meta = getCacheMeta();
    const entry = meta[key];
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(CACHE_PREFIX + key);
      delete meta[key];
      saveCacheMeta(meta);
      return null;
    }

    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    entry.lastAccessed = Date.now();
    saveCacheMeta(meta);

    // Warm memory cache
    memoryCache.set(key, { url: parsed.url, cachedAt: parsed.cachedAt });
    return parsed.url || null;
  } catch {
    return null;
  }
};

/**
 * Resolves a public URL for a 3D asset, using cache when available.
 * @param {string} pathOrUrl - Storage path or full URL
 * @param {Function} [getPublicUrlFn] - Optional function to generate URL from path
 * @returns {string|null}
 */
export const resolveAssetUrl = (pathOrUrl, getPublicUrlFn = null) => {
  if (!pathOrUrl) return null;

  const key = getCacheKey(pathOrUrl);
  const cached = getCachedAssetUrl(key);
  if (cached) return cached;

  // If already a full URL, cache and return
  if (pathOrUrl.startsWith('http')) {
    cacheAssetUrl(key, pathOrUrl);
    return pathOrUrl;
  }

  // Generate via provided function
  if (getPublicUrlFn) {
    const url = getPublicUrlFn(pathOrUrl);
    if (url) cacheAssetUrl(key, url);
    return url || null;
  }

  return pathOrUrl;
};

/**
 * Prefetches and caches multiple asset URLs.
 * Staggers requests to avoid overwhelming slow connections.
 * @param {string[]} urls
 * @param {number} [delayMs] - Delay between prefetch requests (default 200ms)
 */
export const prefetchAssets = (urls = [], delayMs = 200) => {
  const queue = urls.filter(Boolean);
  let index = 0;

  const next = () => {
    if (index >= queue.length) return;
    const url = queue[index++];
    const key = getCacheKey(url);

    // Skip if already cached
    if (getCachedAssetUrl(key)) {
      next();
      return;
    }

    // Use fetch with low priority to prefetch without blocking
    if (typeof fetch !== 'undefined') {
      fetch(url, { method: 'HEAD', mode: 'no-cors' }).catch(() => {});
    }
    cacheAssetUrl(key, url);

    setTimeout(next, delayMs);
  };

  // Delay start to not compete with initial page load
  setTimeout(next, 500);
};

// ─── Progressive Loading ───────────────────────────────────────────────────────

/**
 * Returns a loading strategy based on file size and network quality.
 * @param {number} fileSizeBytes
 * @returns {{ strategy: 'immediate'|'deferred'|'lazy', chunkHint: string }}
 */
export const getLoadingStrategy = (fileSizeBytes = 0) => {
  const quality = getNetworkQuality();
  const sizeMB = fileSizeBytes / (1024 * 1024);

  if (quality === 'slow') {
    if (sizeMB > 5) return { strategy: 'lazy', chunkHint: 'Modèle lourd — chargement différé' };
    if (sizeMB > 2) return { strategy: 'deferred', chunkHint: 'Chargement optimisé réseau lent' };
    return { strategy: 'immediate', chunkHint: '' };
  }

  if (quality === 'medium') {
    if (sizeMB > 15) return { strategy: 'lazy', chunkHint: 'Chargement progressif activé' };
    return { strategy: 'immediate', chunkHint: '' };
  }

  return { strategy: 'immediate', chunkHint: '' };
};

/**
 * Applies texture compression hints to a THREE.js scene.
 * Reduces GPU memory usage on mobile devices.
 * @param {object} scene - THREE.js scene or object3D
 * @param {'low'|'medium'|'high'} quality
 */
export const applyTextureOptimizations = (scene, quality = 'medium') => {
  if (!scene) return;

  const maxAnisotropy = quality === 'high' ? 4 : quality === 'medium' ? 2 : 1;

  scene.traverse?.((child) => {
    if (!child?.isMesh) return;

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((mat) => {
      if (!mat) return;

      // Apply to all texture slots
      const textureSlots = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'];
      textureSlots.forEach((slot) => {
        const tex = mat[slot];
        if (!tex) return;
        tex.anisotropy = maxAnisotropy;
        if (quality === 'low') {
          // Use lower mipmap filtering on slow connections
          tex.minFilter = 512; // THREE.LinearMipmapNearestFilter
          tex.generateMipmaps = true;
        }
        tex.needsUpdate = true;
      });

      // Reduce material complexity on slow connections
      if (quality === 'low') {
        mat.roughness = mat.roughness ?? 0.8;
        mat.metalness = mat.metalness ?? 0.1;
        // Disable expensive features
        if (mat.envMapIntensity !== undefined) mat.envMapIntensity = 0;
      }

      mat.needsUpdate = true;
    });
  });
};

/**
 * Disposes of THREE.js geometries and materials to free GPU memory.
 * Call when unmounting the viewer.
 * @param {object} scene - THREE.js scene or object3D
 */
export const disposeScene = (scene) => {
  if (!scene) return;
  scene.traverse?.((child) => {
    if (child?.geometry) child.geometry.dispose();
    if (child?.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat) => {
        if (!mat) return;
        const slots = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'envMap'];
        slots.forEach((slot) => mat[slot]?.dispose?.());
        mat.dispose();
      });
    }
  });
};

/**
 * Returns cache statistics.
 */
export const getCacheStats = () => {
  const meta = getCacheMeta();
  const entries = Object.values(meta);
  return {
    totalEntries: entries.length,
    memoryEntries: memoryCache.size,
    oldestEntry: entries.length
      ? new Date(Math.min(...entries.map((e) => e.cachedAt))).toLocaleDateString('fr-FR')
      : null,
    networkQuality: getNetworkQuality(),
  };
};

/**
 * Clears all cached asset URLs.
 */
export const clearAssetCache = () => {
  memoryCache.clear();
  try {
    const meta = getCacheMeta();
    for (const key of Object.keys(meta)) {
      localStorage.removeItem(CACHE_PREFIX + key);
    }
    localStorage.removeItem(CACHE_META_KEY);
  } catch {
    // Ignore
  }
};

export default {
  getNetworkQuality,
  getAdaptiveRenderSettings,
  getLoadingStrategy,
  getCacheKey,
  cacheAssetUrl,
  getCachedAssetUrl,
  resolveAssetUrl,
  prefetchAssets,
  applyTextureOptimizations,
  disposeScene,
  getCacheStats,
  clearAssetCache,
};
